import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAnnotations } from './useAnnotations';
import { svgToPng } from '../../utils/svg';

interface UseAnnotationCursorProps {
  canAddAnnotation: boolean;
}

export const useAnnotationCursor = ({ canAddAnnotation }: UseAnnotationCursorProps) => {
  const annotationCursor = useRef<string>('');

  const {
    activeAnnotation,
    activeCriterion,
    activeTask,
    addingAnnotationEnabled,
    canvas,
    cursorDimensions,
    isActiveAnnotationForGrading,
  } = useAnnotations();

  const isAnnotationCursorVisible = useMemo(() => {
    return (
      activeAnnotation &&
      activeCriterion &&
      ((activeCriterion.isScoringAnnotationRequired && isActiveAnnotationForGrading) ||
        !isActiveAnnotationForGrading ||
        (!activeCriterion.isScoringAnnotationRequired && !isActiveAnnotationForGrading)) &&
      activeTask &&
      addingAnnotationEnabled &&
      canAddAnnotation
    );
  }, [
    activeAnnotation,
    activeCriterion,
    activeCriterion?.isScoringAnnotationRequired,
    isActiveAnnotationForGrading,
    activeTask,
    addingAnnotationEnabled,
    canAddAnnotation,
  ]);

  const setCurrentCursor = (forceDefault = false) => {
    const defaultCursor = 'pointer';
    if (!forceDefault && isAnnotationCursorVisible) {
      canvas.defaultCursor = annotationCursor.current;
      canvas.hoverCursor = annotationCursor.current;
      canvas.setCursor(annotationCursor.current);
      canvas.moveCursor = 'move';
    } else {
      canvas.defaultCursor = defaultCursor;
      canvas.hoverCursor = defaultCursor;
      canvas.setCursor(defaultCursor);
      canvas.moveCursor = 'move';
    }
  };

  const setCursorFromSvg = useCallback(async () => {
    if (!activeAnnotation) {
      return;
    }
    try {
      const svgData = await svgToPng(activeAnnotation.svg, 0, true);
      if (activeAnnotation && svgData) {
        const annotationCursorString = `url(${svgData.dataUrl}) ${Math.round(svgData.width / 2)} ${
          svgData.height / 2
        }, auto`;
        annotationCursor.current = annotationCursorString;
        cursorDimensions.current = { width: svgData.width, height: svgData.height };
        setCurrentCursor();
      }
    } catch {
      console.log('error creating cursor');
    }
  }, [activeAnnotation, isAnnotationCursorVisible]);

  useEffect(() => {
    if (!canvas) return;
    if (isAnnotationCursorVisible) {
      setCursorFromSvg();
    } else {
      annotationCursor.current = '';
      cursorDimensions.current = null;
      setCurrentCursor();
    }
  }, [isAnnotationCursorVisible, setCursorFromSvg]);

  return { setCurrentCursor };
};
